/**
 * deviceSignInHint (browser + Electron) — see deviceSignInHint.ts for why this
 * exists and what it may not be used for.
 *
 * localStorage rather than expo-secure-store, which has no browser
 * implementation. That is acceptable precisely because the payload is designed
 * to be boring: a sign-in method and a masked email. Nothing here is a secret,
 * and anything that could be abused by whoever opens the browser next is
 * exactly what the masking removes.
 *
 * Kept API-identical to the native module so callers never branch.
 */
const KEY = 'eleplan.device.signin.hint.v1';

export type SignInMethod = 'password' | 'google' | 'apple' | 'passkey' | 'biometrics';

export interface SignInHint {
  method: SignInMethod;
  emailMasked: string;
  at: number;
}

export const METHOD_LABELS: Record<SignInMethod, string> = {
  password: 'your email and password',
  google: 'Google',
  apple: 'Apple',
  passkey: 'a passkey',
  biometrics: 'biometrics',
};

export function maskEmail(email: string): string {
  const raw = String(email || '').trim();
  const at = raw.lastIndexOf('@');
  if (at <= 0) return '';
  const local = raw.slice(0, at);
  const domain = raw.slice(at);
  if (local.length <= 2) return `${local.slice(0, 1)}•${domain}`;
  const dots = '•'.repeat(Math.min(local.length - 2, 6));
  return `${local.slice(0, 1)}${dots}${local.slice(-1)}${domain}`;
}

function isHint(value: any): value is SignInHint {
  return !!value
    && typeof value === 'object'
    && typeof value.emailMasked === 'string'
    && typeof value.at === 'number'
    && Object.prototype.hasOwnProperty.call(METHOD_LABELS, value.method);
}

export async function rememberSignIn(method: SignInMethod, email: string): Promise<void> {
  try {
    const emailMasked = maskEmail(email);
    if (!emailMasked || typeof localStorage === 'undefined') return;
    const hint: SignInHint = { method, emailMasked, at: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(hint));
  } catch {
    // Private-mode browsers throw on write. A missing hint is the old behaviour.
  }
}

export async function readSignInHint(): Promise<SignInHint | null> {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isHint(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export async function forgetSignInHint(): Promise<void> {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(KEY);
  } catch {
    // Advisory only.
  }
}
